import { Link } from "react-router-dom";
import { useReveal } from "../../../hooks/useReveal";

const benefits = [
  {
    icon: "ri-time-line",
    title: "15+ Years of Mastery",
    desc: "Over a decade and a half capturing once-in-a-lifetime moments across San Antonio and beyond — expertise that shows in every frame.",
  },
  {
    icon: "ri-emotion-happy-line",
    title: "Guided, Stress-Free Experience",
    desc: "Stephanie's calming presence ensures every session flows naturally. No stiff posing — just genuine, beautiful moments.",
  },
  {
    icon: "ri-vip-crown-line",
    title: "Heirloom-Quality Products",
    desc: "Premium fine art prints, albums, and keepsakes engineered to be passed down through generations.",
  },
  {
    icon: "ri-settings-3-line",
    title: "Fully Custom Packages",
    desc: "Every investment is tailored to your vision. No cookie-cutter packages — a plan designed entirely for you.",
  },
];

export default function WhyChooseUs() {
  const ref = useReveal<HTMLElement>();

  return (
    <section ref={ref} className="bg-pk-cream overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* Image — left */}
        <div className="reveal-left relative w-full h-[680px] md:h-auto overflow-hidden">
          <img
            src="https://images-pw.pixieset.com/site/BAJyQr/qLrdZ4/pkheadshotwcamera-004103a6-1500.jpeg"
            alt="Photographer Stephanie — Precious Keepsakes Photography San Antonio"
            className="w-full h-full object-cover object-top"
          />
          {/* Subtle vertical gradient overlap for layering */}
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-pk-cream to-transparent" />

          {/* Floating credential badge */}
          <div className="absolute bottom-10 left-10 bg-white/90 backdrop-blur-sm rounded-sm px-6 py-4 max-w-[220px]">
            <div className="flex items-center gap-2 mb-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-star-fill text-pk-gold" style={{ fontSize: "10px" }} />
                </div>
              ))}
            </div>
            <p className="font-display text-sm italic text-pk-charcoal leading-5">
              &ldquo;She nailed it 100%. I would give 10 stars.&rdquo;
            </p>
            <p className="font-sans text-xs text-pk-muted mt-1">— Lauren &amp; Josh, Wedding Client</p>
          </div>
        </div>

        {/* Content — right */}
        <div className="px-12 md:px-16 lg:px-20 py-20 flex flex-col justify-center">
          <div className="reveal">
            <span className="font-sans text-xs tracking-widest3 uppercase text-pk-gold">
              Why Precious Keepsakes
            </span>
            <h2 className="font-display text-5xl md:text-6xl font-medium text-pk-charcoal mt-4 leading-tight">
              An Experience as Beautiful<br />
              <em>as Your Images</em>
            </h2>
            <p className="font-sans text-sm text-pk-muted mt-6 leading-7 max-w-md">
              I believe the most meaningful photos come from genuine connection. Every session is guided with care, so you can feel relaxed, confident, and fully present in every moment.
            </p>
          </div>

          {/* Signature */}
          <div className="reveal delay-200 my-8 flex items-end gap-4">
            <img
              src="https://images-pw.pixieset.com/site/BAJyQr/9DopZA/signature1-9e15dd4b-1500.png"
              alt="Stephanie — Precious Keepsakes Photography"
              className="h-14 w-auto object-contain"
            />
            <div>
              <p className="font-sans text-xs font-medium text-pk-charcoal uppercase tracking-widest">Stephanie</p>
              <p className="font-sans text-xs text-pk-muted mt-0.5">Founder &amp; Lead Photographer</p>
            </div>
          </div>

          {/* Benefits */}
          <div className="space-y-5 mt-2">
            {benefits.map((b, i) => (
              <div key={i} className={`reveal flex items-start gap-4`} style={{ transitionDelay: `${(i + 2) * 100}ms` }}>
                <div className="w-10 h-10 flex items-center justify-center bg-pk-gold/10 rounded-sm flex-shrink-0 mt-0.5">
                  <i className={`${b.icon} text-pk-gold text-lg`} />
                </div>
                <div>
                  <h3 className="font-sans text-sm font-semibold text-pk-charcoal">{b.title}</h3>
                  <p className="font-sans text-xs text-pk-muted mt-1 leading-5">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="reveal delay-600 mt-10">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 border border-pk-gold text-pk-gold font-sans text-xs tracking-widest3 uppercase px-8 py-3 rounded-sm hover:bg-pk-gold hover:text-white transition-colors duration-300 whitespace-nowrap cursor-pointer w-fit"
            >
              Meet Stephanie
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
